import { FC } from 'react'
import dayjs from 'dayjs'
import { alpha } from '@mui/material/styles'
import { Box, Button, Typography, useMediaQuery, useTheme } from '@mui/material'
import { useIntl } from 'react-intl'
import { useNavigate } from 'react-router-dom'
import R4RIcon from 'components/R4RIcon'
import {
  WidgetCard,
  WidgetCardHeader,
  WidgetCardBody,
  WidgetBadge,
  WidgetOutlineButton
} from './WidgetCard'
import { useLeaseContractsList } from 'services/contracts/hooks/useLeaseContracts'

const WidgetContract: FC = () => {
  const { formatMessage } = useIntl()
  const navigate = useNavigate()
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'))
  const { leaseContractsList } = useLeaseContractsList()

  const activeLeases = leaseContractsList.filter(({ leaseStatus }) => leaseStatus === 0)

  if (!activeLeases.length) {
    return null
  }

  const activeLease = activeLeases.reduce((max, item) => (item.id > max.id ? item : max),
    activeLeases[0]
  )

  const leaseEnd = activeLease.effectiveLeaseEnd || activeLease.leaseEnd

  if (isDesktop) {
    return (
      <WidgetCard sx={{ flex: 1, minWidth: 0 }}>
        <WidgetCardHeader
          icon='document'
          title={formatMessage({ id: 'defectsReportFault.contract', defaultMessage: 'Umowa' })}
          badge={
            <WidgetBadge background={alpha(theme.r4r.cyan, 0.2)} color={theme.r4r.cyan}>
              {formatMessage({ id: 'widgetContract.active', defaultMessage: 'Aktywna' })}
            </WidgetBadge>
          }
        />
        <WidgetCardBody>
          <Typography sx={{ fontSize: '15px', fontWeight: 600, color: theme.r4r.ink }}>
            {activeLease.contractNumber}
          </Typography>
          <Typography sx={{ fontSize: '12px', color: theme.r4r.grey, mt: '2px', pb: '12px' }}>
            {[
              activeLease.leaseStart ? dayjs(activeLease.leaseStart).format('DD.MM.YYYY') : null,
              leaseEnd ? dayjs(leaseEnd).format('DD.MM.YYYY') : null
            ]
              .filter(Boolean)
              .join(' — ')}
          </Typography>
          <WidgetOutlineButton
            onClick={() => navigate(`/additional-services/${activeLease.id}`)}
            sx={{ mt: 'auto' }}>
            {formatMessage({
              id: 'widgetContract.goToContract',
              defaultMessage: 'Przejdź do umowy'
            })}
          </WidgetOutlineButton>
        </WidgetCardBody>
      </WidgetCard>
    )
  }

  return (
    <WidgetCard sx={{ padding: '14px 18px 24px', gap: '9px' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', color: theme.r4r.navy }}>
          <R4RIcon name='document' size={16} />
          <Typography sx={{ fontSize: '15px', fontWeight: 700, color: theme.r4r.navy }}>
            {formatMessage({ id: 'defectsReportFault.contract', defaultMessage: 'Umowa' })}
          </Typography>
        </Box>
        <WidgetBadge background={alpha(theme.r4r.cyan, 0.2)} color={theme.r4r.cyan}>
          {formatMessage({ id: 'widgetContract.active', defaultMessage: 'Aktywna' })}
        </WidgetBadge>
      </Box>
      <Box>
        <Typography sx={{ fontSize: '14px', fontWeight: 600, color: theme.r4r.ink }}>
          {activeLease.contractNumber}
        </Typography>
        <Typography sx={{ fontSize: '11px', color: theme.r4r.grey }}>
          {[
            activeLease.leaseStart ? dayjs(activeLease.leaseStart).format('DD.MM.YYYY') : null,
            leaseEnd ? dayjs(leaseEnd).format('DD.MM.YYYY') : null
          ]
            .filter(Boolean)
            .join(' — ')}
        </Typography>
      </Box>
      <Button
        fullWidth
        variant='outlined'
        onClick={() => navigate(`/additional-services/${activeLease.id}`)}
        endIcon={<R4RIcon name='arrowForward' size={18} />}
        sx={{
          mt: '9px',
          borderColor: theme.r4r.navy,
          color: theme.r4r.navy,
          fontSize: '13px',
          fontWeight: 500,
          minHeight: '28px',
          padding: '5px 13px',
          borderRadius: 0,
          '&:hover': { borderColor: theme.r4r.navy }
        }}>
        {formatMessage({ id: 'widgetContract.goToContract', defaultMessage: 'Przejdź do umowy' })}
      </Button>
    </WidgetCard>
  )
}

export default WidgetContract
